import { getSupabaseClient } from '../lib/supabase';

export interface ScheduledSession {
    id: string;
    guide_id: string;
    seeker_id: string | null;
    session_type: 'consultation' | 'follow_up' | 'assessment';
    session_date: string;
    session_time: string;
    duration_minutes: number;
    session_status: 'scheduled' | 'confirmed' | 'completed' | 'cancelled' | 'no_show';
    notes: string | null;
    seeker_name: string | null;
    seeker_email: string | null;
    seeker_phone: string | null;
    created_at: string;
    updated_at: string;
    start_time: string | null;
    end_time: string | null;
    guide_timezone: string | null;
    seeker_timezone: string | null;
}

export async function fetchSessionsForSeeker(seekerUserId: string): Promise<ScheduledSession[]> {
    const supabase = getSupabaseClient();
    
    const { data, error } = await supabase
        .from('scheduled_sessions')
        .select('*')
        .eq('seeker_id', seekerUserId)
        .order('session_date', { ascending: false })
        .order('session_time', { ascending: false });
    
    if (error) {
        console.error('Error fetching sessions:', error);
        throw error;
    }
    
    return data || [];
}

export function formatSessionDate(date: string): string {
    return new Date(date).toLocaleDateString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    });
}

export function formatSessionTime(time: string): string {
    return new Date(`1970-01-01T${time}`).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
    });
}

/**
 * Get the 3-letter timezone abbreviation for a given timezone and date
 */
function getTimezoneAbbreviation(timezone: string, date: Date): string {
    try {
        // Try shortGeneric first (returns "ET" instead of "EST"/"EDT") — GCS-191
        try {
            const parts = new Intl.DateTimeFormat('en-US', {
                timeZone: timezone,
                timeZoneName: 'shortGeneric'
            }).formatToParts(date);
            const val = parts.find(p => p.type === 'timeZoneName')?.value;
            if (val) return val;
        } catch { /* shortGeneric not supported, fall through */ }
        const formatter = new Intl.DateTimeFormat('en-US', {
            timeZone: timezone,
            timeZoneName: 'short'
        });
        const parts = formatter.formatToParts(date);
        const tzPart = parts.find(p => p.type === 'timeZoneName');
        return tzPart?.value || timezone;
    } catch {
        return timezone;
    }
}

/**
 * Format session date/time using start_time (UTC) and displaying in the specified timezone
 * with the 3-letter timezone abbreviation
 */
export function formatSessionInTimezone(session: ScheduledSession): {
    date: string;
    time: string;
    timezoneAbbrev: string;
} {
    // Prefer start_time (UTC) if available with timezone info
    if (session.start_time) {
        // Use guide_timezone first, then seeker_timezone, then browser timezone
        const timezone = session.guide_timezone || session.seeker_timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
        const startDate = new Date(session.start_time);

        const dateStr = new Intl.DateTimeFormat('en-US', {
            timeZone: timezone,
            weekday: 'short',
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        }).format(startDate);

        const timeStr = new Intl.DateTimeFormat('en-US', {
            timeZone: timezone,
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        }).format(startDate);

        const timezoneAbbrev = getTimezoneAbbreviation(timezone, startDate);

        return { date: dateStr, time: timeStr, timezoneAbbrev };
    }

    // Legacy fallback - use session_date and session_time without timezone info
    return {
        date: session.session_date ? formatSessionDate(session.session_date) : 'Date TBC',
        time: session.session_time ? formatSessionTime(session.session_time) : 'Time TBC',
        timezoneAbbrev: ''
    };
}

export function getSessionStatusBadgeColor(status: string): string {
    switch (status) {
        case 'scheduled':
            return '#3B82F6';
        case 'confirmed':
            return '#10B981';
        case 'completed':
            return '#6B7280';
        case 'cancelled':
            return '#EF4444';
        case 'no_show':
            return '#F59E0B';
        default:
            return '#6B7280';
    }
}

export function getSessionStatusLabel(status: string): string {
    switch (status) {
        case 'scheduled':
            return 'Scheduled';
        case 'confirmed':
            return 'Confirmed';
        case 'completed':
            return 'Completed';
        case 'cancelled':
            return 'Cancelled';
        case 'no_show':
            return 'No Show';
        default:
            return status;
    }
}
